import produce from 'immer';
import {
  GameVariant,
  HexState,
  GameSearch,
  RESOURCES,
  TOKEN_ORDERINGS,
  TOKENS,
  HEX_COUNT_PER_RESOURCE,
  POSITIONS,
  RollValue,
  TOKEN_INFO,
  Interiority,
  ResourceName,
  DesertPlacement,
  GameSearchOptions,
} from './game-info';

export const MAX_GENERATIONS = 1000;

export async function* evolveGameWithThresholds(
  opts: GameSearchOptions,
): AsyncIterableIterator<GameSearch> {

  const {
    collisions: [collisionsLo, collisionsHi],
    adjacencies: [adjacenciesLo, adjacenciesHi],
    imbalance: [imbalanceLo, imbalanceHi],
    variant,
    desertPlacement,
  } = opts;

  let generations = 0;
  let hexes = generateOneGame(variant, desertPlacement);
  let modBy = 1;
  let innerGenerations = 0;

  while (true) {
    const candidates = replicateWithMutations(hexes, 1)
      .map(mutatedHexes => {
        const collisionCount = calculateCollisions(mutatedHexes);
        const imbalanceCount = calculateImbalance(mutatedHexes, variant);
        const adjCount = calculateAdjacencies(mutatedHexes, variant);
        const collisionsScore = scoreWithinRange(collisionCount, collisionsLo, collisionsHi);
        const imbalanceScore = scoreWithinRange(imbalanceCount, imbalanceLo, imbalanceHi);
        const adjScore = scoreWithinRange(adjCount, adjacenciesLo, adjacenciesHi);
        const score = collisionsScore + (imbalanceScore * 4) + adjScore;
        return { hexes: mutatedHexes, imbalanceCount, adjCount, collisionCount, score };
      });
    const best = findBest(candidates, c => -c.score);
    hexes = best.hexes;

    if (
      isWithinRange(best.imbalanceCount, imbalanceLo, imbalanceHi, 'imbalance') &&
      isWithinRange(best.adjCount, adjacenciesLo, adjacenciesHi, 'adjacencies') &&
      isWithinRange(best.collisionCount, collisionsLo, collisionsHi, 'collisions')
    ) {
      yield { ...opts, is: 'done', hexes, generations };
      return;
    } else if (innerGenerations++ >= MAX_GENERATIONS) {
      // best effort
      yield { ...opts, is: 'paused', imperfectHexes: hexes, generations };
      return;
    } else if (generations % modBy === 0) {
      modBy = Math.ceil(modBy ** 1.0004);
      yield { ...opts, is: 'in-progress', generations };
      await wait(1);
    }
    generations++;
  }
}

function findBest<T>(arr: T[], scorer: (t: T) => number): T {
  let bestItem = arr[0];
  let bestScore = scorer(bestItem);
  for (let i=1; i<arr.length; i++) {
    const item = arr[i];
    const score = scorer(item);
    if (score > bestScore) {
      bestScore = score;
      bestItem = item;
    }
  }
  return bestItem;
}

function scoreWithinRange(
  val: number,
  targetLo: number,
  targetHi: number,
): number {
  if (val < targetLo)
    return Math.abs(targetLo - val);
  else if (val > targetHi)
    return Math.abs(targetHi - val);
  else
    return 0;
}

export function isWithinRange(
  val: number,
  targetLo: number,
  targetHi: number,
  which: 'imbalance' | 'collisions' | 'adjacencies',
): boolean {
  const tolerance = which === 'imbalance' ? 0.125 : 0;
  const loWithTolerance = targetLo - tolerance / 2;
  const hiWithTolerance = targetHi + tolerance / 2;
  if (val < loWithTolerance)
    return false;
  else if (val > hiWithTolerance)
    return false;
  else
    return true;
}

/**
 * Mutation function
 * @param hexes hexes
 * @param mutationCount how many swap-mutations. must be >= 1
 */
function replicateWithMutations(
  hexes: HexState[],
  mutationCount: number,
): HexState[][] {
  // const results: HexState[][] = [hexes]; // seemed to get stuck too much
  const results: HexState[][] = [];
  while (results.length < 10) {
    const swaps: [number,number][] = [];
    while (swaps.length < mutationCount) {
      const [a, b] = getTwoRandomIntsInRange(0, hexes.length);
      const aIsDesert = hexes[a].resource === 'desert';
      const bIsDesert = hexes[b].resource === 'desert';
      if (aIsDesert || bIsDesert) continue;
      swaps.push([a, b]);
    }
    results.push(produce(hexes, draft => {
      for (const [a, b] of swaps) {
        const resourceA = hexes[a].resource;
        const resourceB = hexes[b].resource;
        draft[a].resource = resourceB;
        draft[b].resource = resourceA;
      }
    }));
  }
  return results;
}

function getRandomIntInRange(lo: number, hi: number): number {
  if (hi > lo) {
    const diff = hi - lo;
    return lo + Math.floor(Math.random() * diff);
  } else {
    throw new Error('hi not > lo');
  }
}

function getTwoRandomIntsInRange(lo: number, hi: number): [number, number] {
  const a = getRandomIntInRange(lo, hi);
  if (a === lo) {
    const b = getRandomIntInRange(a + 1, hi);
    return [a, b];
  } else if (a === hi - 1) {
    const b = getRandomIntInRange(lo, hi - 1);
    return [b, a];
  } else if (Math.random() > 0.5) {
    const b = getRandomIntInRange(a + 1, hi);
    return [a, b];
  } else {
    const b = getRandomIntInRange(lo, a);
    return [b, a];
  }
}

function wait(t: number): Promise<void> {
  return new Promise(res => setTimeout(res, t));
}

function* mapPositionsToResourcePlacement(
  variant: GameVariant,
  resources: ResourceName[],
  order: number[],
): IterableIterator<[number, { resource: ResourceName, interiority: Interiority }]> {
  for (const position of order) {
    const resource = resources[position];
    const interiority = POSITIONS[variant][position].interiority;
    yield [position, { resource, interiority }];
  }
}

function* zip<A, B>(arrA: A[], arrB: B[]): IterableIterator<[A, B]> {
  const len = Math.min(arrA.length, arrB.length);
  for (let i=0; i<len; i++) {
    yield [arrA[i], arrB[i]];
  }
}

function generateOneGame(
  variant: GameVariant,
  desertPlacement: DesertPlacement,
): HexState[] {
  const resources = shuffle(RESOURCES[variant]);
  const order = chooseRandom(TOKEN_ORDERINGS[variant]);
  if (desertPlacement !== 'anywhere') {
    const positionMap = new Map(mapPositionsToResourcePlacement(variant, resources, order));
    const desertsPositionsNeedingToBeMoved = [...positionMap]
      .filter(([, { resource, interiority }]) => {
        const isDesert = resource === 'desert';
        const isWrongInteriority = interiority !== desertPlacement;
        return isDesert && isWrongInteriority;
      })
      .map(([position, placement]) => position);
    const positionsWhereDesertsCanGo = [...positionMap]
      .filter(([, { resource, interiority }]) => {
        const isNotDesert = resource !== 'desert';
        const isRightInteriority = interiority === desertPlacement;
        return isNotDesert && isRightInteriority;
      })
      .map(([position, placement]) => position);
    const swaps = zip(
      desertsPositionsNeedingToBeMoved,
      shuffle(positionsWhereDesertsCanGo),
    );
    for (const [desertPositionToBeMoved, openPosition] of swaps) {
      const temp = resources[desertPositionToBeMoved];
      resources[desertPositionToBeMoved] = resources[openPosition];
      resources[openPosition] = temp;
    }
  }
  const tokens = TOKENS[variant].slice();
  return order.map((position, idx) => {
    const resource = resources[position];
    const rollValue = resource !== 'desert' ? tokens.shift()! : 7;
    return { resource, rollValue, position };
  });
}

function shuffle<T>(array: T[]): T[] {
  array = array.slice();
  let idx = array.length;
  while (idx) {
    const randomlySelectedIdx = Math.floor(Math.random() * idx--);
    const temp = array[idx];
    array[idx] = array[randomlySelectedIdx];
    array[randomlySelectedIdx] = temp;
  }
  return array;
}

function chooseRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** Higher returns vals are less balanced. */
export function calculateImbalance(hexes: HexState[], variant: GameVariant): number {
  const tallies = {
    lumber: 0,
    grain: 0,
    wool: 0,
    brick: 0,
    ore: 0,
  };
  for (const hex of hexes) {
    if (hex.resource !== 'desert') {
      const hexCount = HEX_COUNT_PER_RESOURCE[variant][hex.resource];
      tallies[hex.resource] += getLikelihood(hex.rollValue) / hexCount;
    }
  }
  const vals = Object.values(tallies);
  return Math.max(...vals) - Math.min(...vals);
}

export function getResourceProbabilities(hexes: HexState[]) {
  const tallies = {
    lumber: 0,
    grain: 0,
    wool: 0,
    brick: 0,
    ore: 0,
  };
  for (const hex of hexes) {
    if (hex.resource !== 'desert') {
      tallies[hex.resource] += getLikelihood(hex.rollValue);
    }
  }
  return tallies;
}

/** Higher returns vals have more adjacencies. */
export function calculateAdjacencies(hexes: HexState[], variant: GameVariant): number {
  let adjCount = 0;
  for (const [prev, next] of traverseHexes(hexes, variant)) {
    if (prev.resource === next.resource) {
      adjCount += 1;
    }
  }
  return adjCount;
}

export function calculateCollisions(hexes: HexState[]): number {
  const noDeserts = hexes.filter(h => h.resource !== 'desert');
  const combos = new Set(noDeserts.map(({ rollValue, resource }) => `${rollValue}:${resource}`));
  return noDeserts.length - combos.size;
}

function* traverseHexes(hexes: HexState[], gameVariant: GameVariant): IterableIterator<[HexState, HexState]> {
  const visited = new Set<HexState>();
  const positions = POSITIONS[gameVariant];
  const hexesByPosition = new Map<number, HexState>();
  for (const hex of hexes) {
    hexesByPosition.set(hex.position, hex);
  }
  const queue: HexState[] = [hexes[0]];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) {
    } else {
      visited.add(current);
      for (const nextPosition of positions[current.position].adjacent) {
        const next = hexesByPosition.get(nextPosition)!;
        if (!visited.has(next)) {
          queue.push(next);
          yield [current, next];
        }
      }
    }
  }
}

export function getLikelihood(rollVal: RollValue): number {
  return TOKEN_INFO[rollVal].likelihood;
}

export function range(n: number): number[] {
  const result: number[] = [];
  for (let i=0; i<n; i++) {
    result.push(i);
  }
  return result;
}

export function rangeBetween(lo: number, hi: number): number[] {
  const result: number[] = [];
  for (let i=lo; i<hi; i++) {
    result.push(i);
  }
  return result;
}

export function capitalize(s: string): string {
  const [first, ...rest] = s;
  return first ? first.toUpperCase() + rest.join('') : s;
}
